import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAds } from '../../redux/features/adsSlice';
import { Line, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const AdAnalyticsPage = () => {
    const dispatch = useDispatch();
    const { items: ads, status } = useSelector(state => state.ads);
    const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'year'

    useEffect(() => {
        if (status === 'idle') {
            dispatch(fetchAds());
        }
    }, [dispatch, status]);

    // Filter active ads
    const activeAds = ads.filter(ad => ad.active);
    
    // Calculate total impressions and clicks
    const totalImpressions = ads.reduce((sum, ad) => sum + (ad.impressions || 0), 0);
    const totalClicks = ads.reduce((sum, ad) => sum + (ad.clicks || 0), 0);
    const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : 0;

    // Performance by ad type
    const adTypeData = {
        labels: ['Banner', 'Sidebar', 'In-feed', 'Popup'],
        datasets: [
            {
                label: 'Impressions',
                data: [
                    ads.filter(ad => ad.type === 'banner').reduce((sum, ad) => sum + (ad.impressions || 0), 0),
                    ads.filter(ad => ad.type === 'sidebar').reduce((sum, ad) => sum + (ad.impressions || 0), 0),
                    ads.filter(ad => ad.type === 'in-feed').reduce((sum, ad) => sum + (ad.impressions || 0), 0),
                    ads.filter(ad => ad.type === 'popup').reduce((sum, ad) => sum + (ad.impressions || 0), 0)
                ],
                backgroundColor: 'rgba(54, 162, 235, 0.5)',
            },
            {
                label: 'Clicks',
                data: [
                    ads.filter(ad => ad.type === 'banner').reduce((sum, ad) => sum + (ad.clicks || 0), 0),
                    ads.filter(ad => ad.type === 'sidebar').reduce((sum, ad) => sum + (ad.clicks || 0), 0),
                    ads.filter(ad => ad.type === 'in-feed').reduce((sum, ad) => sum + (ad.clicks || 0), 0),
                    ads.filter(ad => ad.type === 'popup').reduce((sum, ad) => sum + (ad.clicks || 0), 0)
                ],
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
            }
        ]
    };

    // Best performing ads based on CTR
    const topAds = [...ads]
        .filter(ad => (ad.impressions || 0) > 0)
        .sort((a, b) => {
            const ctrA = ((a.clicks || 0) / (a.impressions || 1)) * 100;
            const ctrB = ((b.clicks || 0) / (b.impressions || 1)) * 100;
            return ctrB - ctrA;
        })
        .slice(0, 5);

    return (
        <div>
            <div className="md:flex md:items-center md:justify-between mb-6">
                <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
                        Ad Analytics
                    </h2>
                </div>
                <div className="mt-4 flex md:ml-4 md:mt-0">
                    <select
                        value={timeRange}
                        onChange={(e) => setTimeRange(e.target.value)}
                        className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                        <option value="week">Last Week</option>
                        <option value="month">Last Month</option>
                        <option value="year">Last Year</option>
                    </select>
                </div>
            </div>

            {status === 'loading' ? (
                <div className="text-center py-10">
                    <div className="spinner"></div>
                    <p className="mt-2 text-gray-600">Loading analytics...</p>
                </div>
            ) : (
                <>
                    <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-3">
                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">
                                                Total Impressions
                                            </dt>
                                            <dd className="text-3xl font-semibold text-gray-900">
                                                {totalImpressions.toLocaleString()}
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">
                                                Total Clicks
                                            </dt>
                                            <dd className="text-3xl font-semibold text-gray-900">
                                                {totalClicks.toLocaleString()}
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">
                                                Average CTR
                                            </dt>
                                            <dd className="text-3xl font-semibold text-gray-900">
                                                {ctr}%
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
                        <div className="bg-white overflow-hidden shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Performance by Ad Type</h3>
                            <div className="h-64">
                                <Bar 
                                    data={adTypeData} 
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        scales: {
                                            y: {
                                                beginAtZero: true
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg p-6">
                            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Top Performing Ads</h3>
                            <div className="overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Ad Title
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Impressions
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Clicks
                                            </th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                CTR
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {topAds.map(ad => (
                                            <tr key={ad.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {ad.title}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {ad.impressions || 0}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {ad.clicks || 0}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {(((ad.clicks || 0) / (ad.impressions || 1)) * 100).toFixed(2)}%
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdAnalyticsPage;
